import { ARROW_ICON, icon, type IconName } from '../components/icons';
import { eyebrow, mesh } from '../components/primitives';
import { sigBand } from '../components/sections';
import { configuredNetworks, SOCIAL_PROFILES } from '../components/social';
import { SITE_CONFIG } from '../config/site-config';
import { EXTERNAL_LINK_ATTRS, mailto, NEW_TAB_NOTE, routeHref } from '../lib/links';

/* ============================ CAREERS ============================ */
type RoleGroup = readonly [
  group: string,
  iconName: IconName,
  roles: readonly (readonly [title: string, summary: string])[],
];

const ROLE_GROUPS: readonly RoleGroup[] = [
  [
    'Engineering &amp; AI',
    'code2',
    [
      ['Senior Software Engineer', 'Lead technical decisions and ship core platform capabilities across the stack.'],
      ['Full-Stack Engineer', 'Build product features end to end, from interface to data and APIs.'],
      ['Frontend Engineer', 'Craft clear, fast and accessible interfaces for complex enterprise workflows.'],
      ['Backend Engineer', 'Design the APIs, data infrastructure and integrations the platform runs on.'],
      ['AI Engineer', 'Build AI agents, orchestration, document intelligence and human-in-the-loop systems.'],
    ],
  ],
  [
    'Marketing &amp; Go-to-Market',
    'trend',
    [
      ['Marketing', 'Shape how ReTax360 tells its story to enterprise property tax organizations.'],
      ['Go-to-Market (GTM)', 'Help bring ReTax360 to new organizations, markets and partners across North America.'],
    ],
  ],
];
const roleMailto = (role: string): string =>
  mailto(SITE_CONFIG.CAREERS_EMAIL, `ReTax360 Careers — ${role.replace(/&amp;/g, '&')}`);
export function pageCareers(): string {
  const areas = [
    'Full-stack engineering',
    'AI / LLM applications',
    'AI agents',
    'Orchestration',
    'APIs',
    'Enterprise integrations',
    'Workflow automation',
    'OCR / document intelligence',
    'Data infrastructure',
    'Cloud architecture',
    'Enterprise security',
  ];
  const soc = configuredNetworks(['linkedin', 'youtube']); // Careers: LinkedIn + YouTube only
  return `
  <section class="hero compact" aria-labelledby="h1">
    ${mesh()}
    <div class="wrap rv">
      ${eyebrow('Careers at ReTax360')}
      <h1 id="h1">Build the infrastructure behind <span class="grad">the future of intelligent work.</span></h1>
      <p class="lede" style="margin-top:24px;max-width:800px">ReTax360 is building an AI orchestration and workflow platform for complex property tax operations. We're looking for talented developers interested in building at the intersection of enterprise software, AI, workflow automation, data and human expertise.</p>
      <p class="lede" style="margin-top:14px;max-width:800px">We're a focused team working on difficult problems—from intelligent document processing and AI orchestration to workflow automation, enterprise integrations and human-in-the-loop systems. If you're a developer who enjoys building real products, solving operational problems and working closely with a fast-moving team, we'd like to hear from you.</p>
      <p class="lede" style="margin-top:14px;max-width:800px">We're also growing our marketing and go-to-market team to bring ReTax360 to property tax organizations across North America.</p>
      <div class="ctas"><a class="btn btn-primary" href="${routeHref('/careers#open-roles')}">See open roles ${ARROW_ICON}</a></div>
    </div>
  </section>
  <section class="s" aria-labelledby="areas-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('Engineering')}<h2 id="areas-h">Software &amp; AI Development.</h2><p>Areas we're interested in include:</p></div>
      <ul class="chipcloud rv">${areas.map((a) => `<li>${a}</li>`).join('')}</ul>
    </div>
  </section>
  <section class="s alt" id="open-roles" aria-labelledby="roles-h">
    <div class="wrap">
      <div class="head rv">${eyebrow("We're hiring")}<h2 id="roles-h">Open roles.</h2><p>We're hiring across engineering, AI, marketing and go-to-market.</p></div>
      ${ROLE_GROUPS.map(
        ([g, groupIcon, roles]) => `
      <div class="role-group rv">
        <h3 class="colh">${g}</h3>
        <ul class="roles">${roles
          .map(
            ([t, d]) => `
          <li class="role"><span class="ic-tile">${icon(groupIcon)}</span><div class="role-t"><h4>${t}</h4><p>${d}</p></div>
            <a class="btn btn-ghost btn-sm" href="${roleMailto(t)}">Apply<span class="sr-only"> for ${t}</span> ${ARROW_ICON}</a></li>`,
          )
          .join('')}
        </ul>
      </div>`,
      ).join('')}
      <p class="fine rv" style="margin-top:20px">Don't see your exact role? We're always interested in hearing from exceptional people. <a class="ul" href="${SITE_CONFIG.CAREERS_MAILTO}">${SITE_CONFIG.CAREERS_EMAIL}</a></p>
    </div>
  </section>

  ${sigBand("We're not building another AI tool. We're building the layer that decides which intelligence does the work, and when a person should.", { dark: true, grad: 'and when a person should.' })}
  <section class="s" aria-labelledby="cw-h">
    <div class="wrap">
      <div class="grid2" style="gap:56px;align-items:start">
        <div class="rv">${eyebrow('How we build the company')}<h2 id="cw-h" style="margin:16px 0">We build our company the way we build our platform.</h2>
          <p class="lede">The principles behind ReTax360 aren't only product principles. They're how we work. The right person owns the right decision, we use the best tools available for the job, and the people closest to the work make the call.</p></div>
        <div class="card rv"><div class="colh">Who thrives here</div><ul class="checks" style="margin-top:0">
          <li>${icon('checkCircle')}<span>Passionate about solving real problems for real professionals</span></li>
          <li>${icon('checkCircle')}<span>Excited to work with the latest AI, cloud and software technology</span></li>
          <li>${icon('checkCircle')}<span>Comfortable making decisions and owning the outcome</span></li>
          <li>${icon('checkCircle')}<span>Moves fast, stays thoughtful, and communicates clearly</span></li>
        </ul></div>
      </div>
      <ul class="grid4" data-stagger style="margin-top:40px">
        <li class="card rv"><span class="ic-tile">${icon('split')}</span><h3>The right intelligence for the right task</h3><p>We match every problem to the right person, tool or approach, and we don't force one answer on everything.</p></li>
        <li class="card rv"><span class="ic-tile">${icon('userCheck')}</span><h3>Human-in-the-loop by design</h3><p>Technology prepares the work. People make the calls that matter, and own them.</p></li>
        <li class="card rv"><span class="ic-tile">${icon('sliders')}</span><h3>Configure. Don't conform.</h3><p>We adapt how we work to the problem in front of us, not the other way around.</p></li>
        <li class="card rv"><span class="ic-tile">${icon('zap')}</span><h3>Build with what's next</h3><p>We work hands-on with modern AI, orchestration and cloud technology, and we keep learning.</p></li>
      </ul>
    </div>
  </section>
  <section class="cta dark" aria-labelledby="ccta-h">
    ${mesh('soft')}
    <div class="wrap rv">
      <h2 id="ccta-h">Interested in building with us?</h2>
      <p>Send your resume, LinkedIn, GitHub or portfolio, the role you're interested in, and a short introduction to <a class="ul" href="${SITE_CONFIG.CAREERS_MAILTO}" style="color:#fff">${SITE_CONFIG.CAREERS_EMAIL}</a>.</p>
      <div class="ctas"><a class="btn btn-primary" href="${SITE_CONFIG.CAREERS_MAILTO}">Contact ReTax360 ${ARROW_ICON}</a></div>
      <p style="margin-top:24px">Don't see your exact role? We're always interested in hearing from exceptional people.</p>
      ${soc.length ? `<p class="learn">Learn more about ReTax360</p><div class="empty-links" style="justify-content:center">${soc.map((k) => `<a class="tlink" href="${SOCIAL_PROFILES[k].url()}" ${EXTERNAL_LINK_ATTRS}>${icon(k, 'si')}${SOCIAL_PROFILES[k].name} ${icon('arrow')}${NEW_TAB_NOTE}</a>`).join('')}</div>` : ''}
    </div>
  </section>`;
}
